import type { Stripe } from '@stripe/stripe-js'

import { CreditCardSaveGateway } from '#/shared/api/generated/billing/models'
import type {
  TokenizeBootstrap,
  TokenizeBootstrapGateway,
} from '#/shared/api/generated/billing/models'
import { useGetTokenizeBootstrapApiV1CardsTokenizeGet } from '#/shared/api/generated/billing/theCargoBilling'
import type { AuthnetConfig } from '#/shared/config/authorize-net'
import { getStripe } from '#/shared/config/stripe'

export interface CardBootstrap {
  gateway: TokenizeBootstrapGateway | undefined
  stripe: Promise<Stripe | null> | undefined
  authnet: AuthnetConfig | undefined
  isLoading: boolean
  isUnavailable: boolean
}

function resolvePublishableKey(
  bootstrap: TokenizeBootstrap | undefined,
  gateway: TokenizeBootstrapGateway | undefined,
): string | undefined {
  if (gateway !== CreditCardSaveGateway.stripe) return undefined
  return bootstrap?.publishable_key ?? undefined
}

function resolveAuthnet(
  bootstrap: TokenizeBootstrap | undefined,
  gateway: TokenizeBootstrapGateway | undefined,
): AuthnetConfig | undefined {
  if (gateway !== CreditCardSaveGateway.authorize_net) return undefined
  if (!bootstrap?.api_login_id || !bootstrap.client_key) return undefined
  return {
    authData: {
      apiLoginID: bootstrap.api_login_id,
      clientKey: bootstrap.client_key,
    },
    environment: bootstrap.environment ?? 'sandbox',
  }
}

export function useCardBootstrap(): CardBootstrap {
  const { data, isLoading, isError } =
    useGetTokenizeBootstrapApiV1CardsTokenizeGet({
      query: { staleTime: 0, gcTime: 0, refetchOnMount: 'always' },
    })
  const bootstrap = data?.data
  const gateway = bootstrap?.gateway ?? undefined
  const publishableKey = resolvePublishableKey(bootstrap, gateway)
  const authnet = resolveAuthnet(bootstrap, gateway)
  const hasUsableGateway = Boolean(publishableKey) || Boolean(authnet)

  return {
    gateway,
    stripe: publishableKey ? getStripe(publishableKey) : undefined,
    authnet,
    isLoading,
    isUnavailable: isError || (!isLoading && !hasUsableGateway),
  }
}
